<template lang="pug">
.slides.slides.dont-unlock.col-span-edge.relative.z-0(
  :data-completed="screen.id ? suspend.completed[screen.id] : null"
  ref="elRef"
)
  //- expose these strings for the translate script
  //- text="Slide ${i} of ${n}"
  nav.absolute.z-10.flex.items-center.justify-center.w-full.top-4.mx-auto
    .flex.rounded-full.px-1(
      class="bg-black/10 dark_bg-white/20"
    )
      .page-icon(
        v-for="(s, i) in screen.slides"
      )
        button.bg-transparent.p-2.group(
          :disabled="!unlocked(i)"
          :aria-label="`${slideAriaLabel(i)}` + `${s.title ? ': ' + s.title.trim() : ''}`"
          @click="() => open(i)"
          v-if="i > 0 || !s.start"
        )
          .w-3.h-3.rounded-full.border-2.group-focus-visible_ring-4(
            :class="['border-black/50 dark_border-white/50', { 'bg-black/55 border-black/60 dark_bg-white/70 dark_border-white/80': currentIndex === i, 'opacity-20': !unlocked(i) }]"
          )

  .slide(
    :class="[{ 'slide--active': currentIndex === i }]"
    :id="getSlideId(i)"
    :variant-slide="getSlideVariant(s)"
    key="i"
    v-for="(s, i) in screen.slides"
  )
    UseLockables(
      :status-ref="addSlideLockableStatus"
      :id="screen.id && getSlideId(i)"
      v-slot="{ locked: slideLocked }"
    )
      provide-section-id(:section-id="getSlideId(i)")
        .slide-container(v-if="currentIndex === i || audioDoc || wordDoc")
          audio-marker(:name="getSlideId(i)")
          uc-screen.py-16(
            :nested="true"
            :show-nested-bg="Boolean(s.style || s.bg)"
            :edit-path="`${editPath}.slides.${i}`"
            :screen="{ fullscreen: true, 'no-fade': true, ...s }"
          )
</template>

<script setup lang="ts">
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  provide,
  ref,
  unref,
  watch
} from 'vue';
import {
  getCompleted,
  getViewed,
  setCompleted,
  setViewed,
  set
} from '../lib/SCORM';
import {
  play,
  stop,
  queue,
  playNextInQueue,
  playUrlSequence
} from '../lib/audio';
import useAudio from '../stores/useAudio';
import {
  resolveSlideAudioUrls,
  useChildScreenAudio,
  type NarratableScreen
} from '../lib/useScreenAudio';
import useSuspend from '../lib/useSuspend';
import isQuizPage from '../lib/isQuizPage';
import { inject } from 'vue';
import ScrollTrigger from 'gsap/ScrollTrigger';
import useTheme from '../stores/useTheme';
import {
  UseLockables,
  useCompletables,
  useLockables
} from '../stores/useLockables';
import bugAlert from '../lib/bugAlert';
import useTranslations from '../stores/useTranslations';

interface SlidesHTMLElement extends HTMLDivElement {
  next: () => void;
  // prev: () => void;
}

const elRef = ref<SlidesHTMLElement>();
const suspend = useSuspend();
const props = defineProps<{
  screen: ScreenSlides;
  editPath: string;
}>();

const { translations } = useTranslations();

const provideSectionId = defineComponent({
  props: { sectionId: { type: String, required: true } },
  setup(props, { slots }) {
    provide('sectionId', props.sectionId);
    return () => [h('div', slots.default())];
  }
});

const { getAudioURL } = useAudio();
const { audioAiJitEnabled } = useChildScreenAudio();
const audioDoc = inject('audioDoc');
const wordDoc = inject('wordDoc');

useCompletables(props.screen.id);
const { locked } = useLockables(props.screen.id);
const sectionId = inject('sectionId') as string;
const pageId = inject('pageId') as object;

// check for screen and slide IDs
if (!props.screen.id) {
  bugAlert(`Slides missing an id in #${sectionId}`);
}

props.screen.slides.forEach((s, i) => {
  if (!s.id)
    bugAlert(`Slide missing an id in ${props.screen.id}. Slide: ${i + 1}`);
});

const getSlideId = (i: number) => `${props.screen.id}_slide-${i}`;
const getSlideVariant = s =>
  s['variant-slide'] ??
  props.screen['variant-slide'] ??
  theme.value['variant-slide'];
const theme = useTheme();

const slideLockables = ref(new Map<string, LockableStatus>());

const addSlideLockableStatus = (status: LockableStatus) => {
  slideLockables.value.set(status.id, status);
};

const checkCompletion = () => {
  const screenId = props.screen.id;
  const allViewed = props.screen.slides.every(
    (_, i) => getViewed(getSlideId(i)) || getCompleted(getSlideId(i))
  );

  if (allViewed && !locked.value) {
    if (screenId) setCompleted(screenId);
    setCompleted(sectionId);
  }

  if (!screenId) {
    setCompleted(sectionId);
  }
};

// update content completion when lockable status changes
watch(locked, (curr, prev) => {
  console.log('watch locked', curr, prev);
  if (prev && !curr) checkCompletion();
});

let isQuizReset = ref(false);
const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e, pageId)) reset();
  isQuizReset.value = true;
  open(0, { triggerAudio: false }, { useScroller: false });
});

let currentIndex = ref(0);

// Tracks the slides' ScrollTrigger active state so an in-flight JiT resolve can
// be discarded if the user scrolls away before synthesis completes.
const slidesActive = ref(false);

watch(currentIndex, () => {
  if (!isQuizReset.value) {
    ScrollTrigger.refresh();
  }
});

/**
 * JiT slide audio URLs in play order: a `templates[]` chain, a split's panel
 * intros, or a single child intro. Splits and `templates[]` carry their
 * narration in nested screens, not the slide's own intro slot, so they need the
 * dedicated resolvers (shared with screen-carousel.vue via resolveSlideAudioUrls).
 */
const resolveSlideJitUrls = (i: number): Promise<string[]> => {
  const slide = props.screen.slides[i];
  if (!slide) return Promise.resolve([]);

  return resolveSlideAudioUrls(
    slide as NarratableScreen,
    props.screen as unknown as NarratableScreen,
    {
      slideId: slide.id ?? getSlideId(i),
      editPathBase: `${props.editPath}.slides.${i}`
    }
  );
};

/** Play the first resolved slide URL and queue the rest as a sequence. */
const playSlideJitUrls = (urls: string[]) => {
  playUrlSequence(urls, { delay: 1.2, owner: props.screen.id });
};

const open = async (
  i: number,
  { triggerAudio = true } = {},
  { useScroller = true } = {}
) => {
  const slide = props.screen.slides[i];
  const id = getSlideId(i);

  currentIndex.value = i;

  setViewed(id);

  const slideLockable = slideLockables.value.get(id);

  // console.log(
  //   'OPEN slideLockable:',
  //   id,
  //   'locked:',
  //   slideLockable?.locked.value
  // );

  if (slideLockable?.locked.value === false) {
    setCompleted(id);
    if (slide.id) setCompleted(slide.id);
  }

  checkCompletion();

  await nextTick();
  await nextTick();

  const slideEl = document.getElementById(getSlideId(i));
  const slideScreen = slideEl?.querySelector('.screen');
  const scroller = document.querySelector('.scroller') as HTMLElement;
  const slides = unref(elRef);

  if (scroller && slides && useScroller) {
    // scroll back up for the next slide
    gsap.to(scroller, {
      scrollTo: { y: slides },
      duration: 0.5
    });
  }

  if (triggerAudio) {
    stop();

    if (slide.audio) {
      play(slide.audio);
    } else if (slide.speech) {
      play(slide.speech);
    } else if (audioAiJitEnabled.value) {
      // JiT path: resolve the slide's audio sequence (split panels / templates
      // chain / single intro) and play in order. Falls back to the slide's
      // audio-marker ID if no YAML id is set.
      resolveSlideJitUrls(i).then(urls => {
        // Bail if the user moved to another slide while JiT was synthesizing.
        if (currentIndex.value !== i) return;
        playSlideJitUrls(urls);
      });
    } else {
      // S3 path: play the slide's audio marker, queue any nested data-queue-audio elements
      const elsToQueue = slideScreen?.querySelectorAll('[data-queue-audio]');
      const audio = getAudioURL(getSlideId(i));
      const slideAudio = audio ? String(audio) : '';

      if (elsToQueue) {
        elsToQueue.forEach(q => {
          const queueAudio = q.getAttribute('data-queue-audio');
          if (queueAudio) queue(queueAudio);
        });
      }

      if (slideAudio !== '') {
        play(slideAudio, { delay: 1.2 });
      } else if (elsToQueue?.length) {
        playNextInQueue();
      }
    }
  }

  nextTick().then(() => {
    if (useScroller)
      elRef.value?.querySelector<HTMLElement>(`[data-dot="${i}"]`)?.focus();
  });
};

const unlocked = (i: number) =>
  currentIndex.value === i ||
  getViewed(getSlideId(i)) ||
  getCompleted(getSlideId(i));

const next = () => {
  const i = (currentIndex.value + 1) % props.screen.slides.length;
  isQuizReset.value = false;
  open(i);
};

/*const prev = async () => {
  const i =
    (currentIndex.value + props.screen.slides.length - 1) %
    props.screen.slides.length;

  open(i);
};*/

const slideAriaLabel = function (i: number) {
  const translation =
    hasTranslation('Slide ${i} of ${n}') || 'Slide ${i} of ${n}';
  return translation
    .replace('${i}', i)
    .replace('${n}', String(props.screen.slides.length));
};

function hasTranslation(key: string) {
  // get the translation if it exists
  return translations?.[suspend.locale]?.[key] || key;
}

const reset = () => {
  set(`completed.${props.screen.id}`, false);
  props.screen.slides.forEach((s, i) => {
    set(`completed.${getSlideId(i)}`, false);
    set(`viewed.${getSlideId(i)}`, false);
  });
};

onMounted(() => {
  const el = unref(elRef);
  const page = el.closest('.page');

  if (el) {
    el.next = next;
    // el.prev = prev;
  }

  if (page) {
    ScrollTrigger.create({
      scroller: el.closest('.scroller'),
      trigger: el,
      start: 'top 45%',
      end: 'bottom 55%',

      async onToggle({ isActive }) {
        slidesActive.value = isActive;
        await nextTick();

        if (isActive && el.offsetHeight > 0) {
          if (audioAiJitEnabled.value) {
            // JiT path: resolve the current slide's audio sequence (split panels
            // / templates chain / single intro) and play in order.
            const i = currentIndex.value;
            const urls = await resolveSlideJitUrls(i);
            // Bail if we left the slides or changed slide while synthesizing.
            if (!slidesActive.value || currentIndex.value !== i) return;
            playSlideJitUrls(urls);
          } else {
            // S3 path: play audio marker + queue any nested data-queue-audio elements
            const slideScreen = el?.querySelector('.screen');
            const elsToQueue =
              slideScreen?.querySelectorAll('[data-queue-audio]');

            if (elsToQueue) {
              elsToQueue.forEach(q => {
                const queueAudio = q.getAttribute('data-queue-audio');
                if (queueAudio) queue(queueAudio);
              });
            }

            const audio = getAudioURL(getSlideId(currentIndex.value));
            const slideAudio = audio ? String(audio) : '';

            if (slideAudio !== '') {
              play(slideAudio, { delay: 1.2 });
            } else if (elsToQueue?.length) {
              playNextInQueue();
            }
          }
        }
      }
    });
  }

  open(0, { triggerAudio: false }, { useScroller: false });
});
</script>

<style>
.screen--slides {
  & > .screen-grid > .display-yaml {
    display: none;
  }

  &.screen .screen .display-yaml {
    display: block !important;
  }

  .screen-grid {
    margin: 0;
  }
}

/* override the bg auto-hide for nested screens */
.screen .screen {
  &:where([data-show-nested-bg='true']) {
    .screen__fill {
      @apply !block;
    }
  }

  /*:data-bg-contain="s.style?.includes('bg-contain')"*/
  /*&:where([data-bg-contain]) {
    .screen__fill {
      img:first-of-type {
        object-fit: contain;
      }
    }
  }*/
}
</style>
